/**
 * 「今の会社」（複数の会社に所属するアカウントのフェーズ 2・2026-10-17）。
 * 設計: docs/04_機能仕様/複数の会社に所属するアカウント設計.md §9
 *
 * - 今の会社 ID は**タブのメモリ**に持つ（localStorage に置かない）。トークンと同じく localStorage から読むと、
 *   別のタブの切り替えと同時に変わり、サーバの 409 COMPANY_CONTEXT_MISMATCH が一度も発火しない
 * - 会社はサーバがトークンで決める。画面は `X-Company-ID` で「このタブが受け取った会社」を送って照合させるだけ
 * - ここに置くのは DOM に触れない状態と判定だけ（テストできるように）。トークンの保存は authToken.ts
 */

export type RoleType = 'ADMIN' | 'STAFF' | 'CAST';

/** `/api/user`・ログイン・切り替えの応答の `companies[]`（サーバの AccountMembershipService::companiesPayload） */
export interface CompanyMembership {
  company_id: number;
  company_name: string | null;
  employee_id: number;
  role_type: RoleType;
  display_name?: string;
  dashboard_path: string;
  stores: { id: number; store_cd?: string | null; store_name: string }[];
  on_duty_store_ids: number[];
}

let currentCompanyId: number | null = null;
let memberships: CompanyMembership[] = [];
let switching = false;

export function getCurrentCompanyId(): number | null {
  return currentCompanyId;
}

export function setCurrentCompanyId(id: unknown): void {
  const n = typeof id === 'number' ? id : Number(id);
  currentCompanyId = Number.isInteger(n) && n > 0 ? n : null;
}

export function getMemberships(): CompanyMembership[] {
  return memberships;
}

export function setMemberships(list: unknown): void {
  memberships = Array.isArray(list) ? (list as CompanyMembership[]) : [];
}

/** 今の会社の所属（無ければ null。古い API で companies が無いときも null） */
export function getCurrentMembership(): CompanyMembership | null {
  return memberships.find(m => m.company_id === currentCompanyId) ?? null;
}

/**
 * 切り替え中のフラグ。切り替えの要求**以外**の 401 / 409 を握る（解決しない Promise を返す）のに使う。
 * サーバは古いトークンを 60 秒後に失効させるので、切り替えの直前に出た定期取得の 401 でログアウトしない。
 * 🔴 切り替えの要求そのものは握らない（失敗したら理由を出して、必ずフラグを下ろす）
 */
export function isSwitchingCompany(): boolean {
  return switching;
}

export function beginCompanySwitch(): void {
  switching = true;
}

export function endCompanySwitch(): void {
  switching = false;
}

/** テスト用: 状態を初めに戻す */
export function resetCompanyContextForTest(): void {
  currentCompanyId = null;
  memberships = [];
  switching = false;
}

const ROLE_HOME: Record<RoleType, string> = {
  ADMIN: '/admin/dashboard',
  STAFF: '/staff/dashboard',
  CAST: '/cast/home',
};

/**
 * ログイン（店を選んだ後を含む）の行き先。
 * `redirect` は**その会社での役割**に合うときだけ使う（A では管理者・B ではキャストの人が、B で /admin に入らないように）。
 * 外部の URL・`//` 始まり・/login は使わない
 */
export function resolvePostLoginDestination(
  roleType: RoleType | null | undefined,
  dashboardPath: string | null | undefined,
  redirect: unknown,
  /** 兼務のフラグ（LINE のログイン。is_staff のキャストはスタッフの画面へ戻れる） */
  opts?: { isStaff?: boolean },
): string {
  const home = (roleType && ROLE_HOME[roleType]) || dashboardPath || '/admin/dashboard';
  if (!isSameOriginPath(redirect) || redirect === '/login' || redirect.startsWith('/select-store')) {
    return home;
  }
  const isAdminPath = redirect.startsWith('/admin/');
  const isStaffPath = redirect.startsWith('/staff/');
  const isCastPath = redirect.startsWith('/cast/');
  const allowed =
    (isAdminPath && roleType === 'ADMIN') ||
    (isStaffPath && (roleType === 'STAFF' || !!opts?.isStaff)) ||
    (isCastPath && roleType === 'CAST') ||
    (!isAdminPath && !isStaffPath && !isCastPath);

  return allowed ? redirect : home;
}

/**
 * 同じサイトの中のパスか。`/` で始まり、`//`・`/\`（URL の解釈では `\` が `/` として扱われ、外部のホストになる）・
 * 制御文字を含まない。外部へ飛ばす戻り先（オープンリダイレクト）を断る
 */
export function isSameOriginPath(redirect: unknown): redirect is string {
  // `?redirect=/a&redirect=/b` は配列で来る（router.query の `as string` は型を書くだけ）。例外にせず「戻り先なし」にする
  // （例外にすると、ログイン画面の catch がトークンを消してログアウトさせる）
  if (typeof redirect !== 'string' || !redirect.startsWith('/')) return false;
  if (redirect.startsWith('//')) return false;
  if (redirect.includes('\\')) return false;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(redirect)) return false;
  try {
    const base = 'https://logme.invalid';
    return new URL(redirect, base).origin === base;
  } catch {
    return false;
  }
}

/** 通信の共通処理（axios.ts）が、失敗した応答をどう扱うか */
export type ResponseErrorAction = 'hold' | 'reload-company' | 'reload-store' | 'clear-token' | 'pass';

/**
 * 会社・店の食い違い（409）の見分け。理由コードは error_code / code / error のどれでも受ける
 */
export function isContextMismatch(status: unknown, data: any): 'company' | 'store' | null {
  if (status !== 409 || !data) return null;
  const code = data.error_code ?? data.code ?? data.error;
  if (code === 'COMPANY_CONTEXT_MISMATCH') return 'company';
  if (code === 'STORE_CONTEXT_MISMATCH') return 'store';
  return null;
}

/**
 * 401 でトークンを消してよいか。消さないのは「その要求が送ったトークン」と「今のトークン」が違う（もう新しいトークンに替わった）ときだけ。
 * 切り替え・ログインで新しいトークンに替わった後に、古いトークンの要求の失敗が遅れて届くと、新しいトークンまで消していた
 */
export function shouldClearToken(sentToken: string | null | undefined, currentToken: string | null | undefined): boolean {
  return !(sentToken && currentToken && sentToken !== currentToken);
}

/**
 * 失敗した応答の扱い（axios.ts の応答インターセプタの判定。テストできるようにここに置く）。
 * - 会社・店の食い違い（409）は**先頭で**受けて読み直す（画面の 409 の処理〈退勤の「すでに退勤済みです」など〉に渡さない）
 * - 切り替えの最中は、切り替えの要求**以外**の 401 / 409 を握る（古いトークンの定期取得でログアウトしない）
 * - 🔴 切り替えの要求そのものは握らない（握ると「切り替えています…」のまま固まり、フラグも下りない）
 */
export function classifyResponseError(p: {
  status: unknown;
  data: any;
  isSwitchRequest: boolean;
  switching: boolean;
  sentToken: string | null | undefined;
  currentToken: string | null | undefined;
}): ResponseErrorAction {
  const mismatch = isContextMismatch(p.status, p.data);
  if (mismatch && !p.isSwitchRequest) {
    if (p.switching) return 'hold';
    return mismatch === 'company' ? 'reload-company' : 'reload-store';
  }
  if (p.status === 401) {
    if (p.switching && !p.isSwitchRequest) return 'hold';
    return shouldClearToken(p.sentToken, p.currentToken) ? 'clear-token' : 'pass';
  }
  return 'pass';
}

/**
 * 店の食い違い（409）で保存値を消した後に、書き戻す店。
 * その端末でその会社で前回選んだ店が、今の会社で入れる店で、断られた店と違えばそれ。無ければ null（StoreContext が選び直す）
 */
export function storeToRestore(
  rejectedStoreId: string | null | undefined,
  lastStoreIdForCompany: string | null | undefined,
  membership: CompanyMembership | null,
): { id: number; name: string } | null {
  if (!lastStoreIdForCompany || !membership) return null;
  if (rejectedStoreId && String(rejectedStoreId) === String(lastStoreIdForCompany)) return null;
  const store = membership.stores.find(s => String(s.id) === String(lastStoreIdForCompany));
  return store ? { id: store.id, name: store.store_name } : null;
}

/**
 * トークンを受け取ったときに、端末に残っている「選んでいる店」を残してよいか。
 * 今の会社で入れる店に無ければ消す（別の人・別の会社で入った端末に古い店が残ると、全部の要求が 409 STORE_CONTEXT_MISMATCH になる）。
 * companies が無い（古い API・会社のセットアップ前）ときは判断しない＝残す
 */
export function keepSelectedStore(
  selectedStoreId: string | number | null | undefined,
  currentId: number | null,
  list: CompanyMembership[],
): boolean {
  if (selectedStoreId === null || selectedStoreId === undefined || selectedStoreId === '') return false;
  if (list.length === 0 || currentId === null) return true;
  const current = list.find(m => m.company_id === currentId);
  if (!current) return true;
  return current.stores.some(s => String(s.id) === String(selectedStoreId));
}

export interface StoreChoice {
  companyId: number;
  companyName: string;
  roleType: RoleType;
  storeId: number | null; // null = 店が 0 件の会社（配属待ち）の行
  storeName: string;
  onDuty: boolean;
}

/**
 * 「どのお店で使いますか」の並び。会社ごとにまとめ、その端末で前回選んだ店を一番上に出す。
 * 店が 0 件の会社（作ったばかりの 2 社目・配属待ち）は会社の行を 1 つ出す（無いと入る手段が無い）
 */
export function buildStoreChoices(list: CompanyMembership[], lastStoreId: number | null): { last: StoreChoice | null; groups: { companyId: number; companyName: string; choices: StoreChoice[] }[] } {
  let last: StoreChoice | null = null;
  const groups = list.map(m => {
    const companyName = m.company_name || 'お店';
    const choices: StoreChoice[] = m.stores.length > 0
      ? m.stores.map(s => ({
          companyId: m.company_id,
          companyName,
          roleType: m.role_type,
          storeId: s.id,
          storeName: s.store_name,
          onDuty: (m.on_duty_store_ids || []).includes(s.id),
        }))
      : [{ companyId: m.company_id, companyName, roleType: m.role_type, storeId: null, storeName: 'お店がまだありません（配属待ち）', onDuty: false }];
    if (lastStoreId !== null && last === null) {
      last = choices.find(c => c.storeId === lastStoreId) ?? null;
    }
    return { companyId: m.company_id, companyName, choices };
  });

  return { last, groups };
}

/**
 * 別の会社のお店に切り替えるときの確認の文面（設計書 §9.3。誤って押して伝票・受付が別の会社に入るのを防ぐので、必ず出す）。
 * 出勤中の店から離れるときはそれも書く（退勤を忘れる・別の店で出勤を押すのを防ぐ）。未保存の入力は全部（会社設定も）並べる
 */
export function companySwitchConfirmOptions(
  toCompanyName: string,
  toStoreName: string | null,
  leavingOnDutyStoreNames: string[],
  unsavedLabels: string[],
): { title: string; description: string; confirmLabel: string; cancelLabel: string } {
  const lines: string[] = [
    toStoreName
      ? `「${toStoreName}」（${toCompanyName}）に切り替えます。`
      : `${toCompanyName} に切り替えます（お店がまだありません）。`,
    'このあとの受付・伝票・打刻は、切り替えた先のお店に入ります。',
  ];
  if (leavingOnDutyStoreNames.length > 0) {
    lines.push('', `いま「${leavingOnDutyStoreNames.join('」「')}」で出勤中です。退勤を忘れないでください。`);
  }
  if (unsavedLabels.length > 0) {
    const shown = unsavedLabels.slice(0, 6);
    const more = unsavedLabels.length - shown.length;
    lines.push('', '次の項目は、まだ保存していない入力が消えます。', ...shown.map(l => `・${l}`), ...(more > 0 ? [`・ほか ${more} 件`] : []));
  }

  return {
    title: `${toCompanyName} のお店に切り替えます`,
    description: lines.join('\n'),
    confirmLabel: '切り替える',
    cancelLabel: 'このお店のままにする',
  };
}

/** 端末で前回選んだ店（ユーザーごと。ログアウトの全消しから外す） */
export const LAST_STORE_KEY_PREFIX = 'logme:last_store:';

/**
 * 会社を付けると「その会社で前回選んだ店」のキー（店の 409 から戻す先に使う）。付けなければ会社をまたいだ前回の店（店選びの並びに使う）。
 * 会社ごとに持つのは、古いタブで別の会社の店を選んだときに、正しいタブの戻し先が上書きされないようにするため
 */
export function lastStoreKey(userId: number | string, companyId?: number | string | null): string {
  return companyId ? `${LAST_STORE_KEY_PREFIX}${userId}:${companyId}` : `${LAST_STORE_KEY_PREFIX}${userId}`;
}
